import { text } from 'stream/consumers'

import React from 'react'

import clsx from 'clsx'

import { Button, EnumButtonVariants, Tooltip, IToggler } from '@insquad/tools'

import { ChevronIcon } from 'src/common/components/icons/ChevronIcon'
import { CloseIcon } from 'src/common/components/icons/CloseIcon'
import { ShareIcon } from 'src/common/components/icons/ShareIcon'
import {
  Typography,
  EnumTypographyVariants,
} from 'src/common/components/Typography/Typography'
import { Resume } from 'src/graphql/types'

import { CandidateResumeEducation } from './CandidateResumeEducation/CandidateResumeEducation'
import { CandidateResumeInfoFull } from './CandidateResumeInfoFull/CandidateResumeInfoFull'
import { CandidateResumeOtherExperience } from './CandidateResumeOtherExperience/CandidateResumeOtherExperience'
import { CandidateResumeProjectExpectations } from './CandidateResumeProjectExpectations/CandidateResumeProjectExpectations'
import { CandidateResumeSkills } from './CandidateResumeSkills/CandidateResumeSkills'
import { CandidateResumeTechnologies } from './CandidateResumeTechnologies/CandidateResumeTechnologies'
import { CandidateResumeVettedSkills } from './CandidateResumeVettedSkills/CandidateResumeVettedSkills'
import { CandidateResumeVideo } from './CandidateResumeVideo/CandidateResumeVideo'
import { CandidateResumeWorkExperience } from './CandidateResumeWorkExperience/CandidateResumeWorkExperience'
import { ResumeHireBanner } from './ResumeHireBanner/ResumeHireBanner'
import { ResumeLoginBanner } from './ResumeLoginBanner/ResumeLoginBanner'

import s from './ResumeDrawerContent.module.scss'

const getButtons = () => [
  {
    children: <ShareIcon />,
  },
  { children: 'Ask a question' },
  {
    children: 'Book a call',
  },
]

export type ResumeDrawerContentProps = {
  resume: Resume
  hintModalToggler: IToggler
  drawerElement: HTMLElement | null
  onClose: () => void
}

export const ResumeDrawerContent: React.FC<ResumeDrawerContentProps> = ({
  resume,
  onClose,
  drawerElement,
  hintModalToggler,
}) => {
  const renderButtons = (className?: string) => {
    return (
      <div className={clsx(s.ResumeDrawerContent__btns, className)}>
        {getButtons().map(({ children }, i) => (
          <Tooltip
            key={i}
            usePopperConfig={{ placement: 'top', offset: [0, 20] }}
            tooltipContent="Sign up to access all functions"
            contentMaxWidth={285}
          >
            {({ setTriggerRef }) => (
              <div
                ref={setTriggerRef}
                className={s.ResumeDrawerContent__btn_block}
              >
                <Button className={s.ResumeDrawerContent__btn} disabled>
                  {children}
                </Button>
              </div>
            )}
          </Tooltip>
        ))}
      </div>
    )
  }

  return (
    <div className={s.ResumeDrawerContent}>
      <div className={s.ResumeDrawerContent__header}>
        <Typography
          variant={EnumTypographyVariants.P1F}
          className={s.ResumeDrawerContent__goBack}
          onClick={onClose}
          as="div"
        >
          <div className={s.ResumeDrawerContent__goBackIcon}>
            <ChevronIcon />
          </div>
          Back to candidates
        </Typography>

        <div className={s.ResumeDrawerContent__tablet}>{renderButtons()}</div>
      </div>

      <ResumeHireBanner resumeId={resume.id} />
      <CandidateResumeInfoFull
        experience={resume.totalExperience}
        firstName={resume.firstName}
        price={resume.price}
        lastName={resume.lastName}
        overlap={resume.overlap}
        photo={resume.photo}
        englishLevel={resume.englishLevel}
        specialization={resume.specialization}
        renderButtons={renderButtons}
      />
      <CandidateResumeTechnologies
        languages={resume.Languages}
        technologies={resume.Technologies}
        vettedSkills={resume.VettedSkills}
      />
      <CandidateResumeOtherExperience
        technologies={resume.Technologies}
        languages={resume.Languages}
        work={resume.WorkHistory}
        vettedSkills={resume.VettedSkills}
      />
      <CandidateResumeVideo
        videoLink={resume.videoLink}
        photo={resume.photo}
        interviewer={resume.Interviewer}
      />
      <CandidateResumeVettedSkills
        vettedSkills={resume.VettedSkills}
        hintModalToggler={hintModalToggler}
        modalBlackoutPortalParent={drawerElement}
        modalBodyPortalParent={drawerElement}
      />
      <CandidateResumeSkills text={resume.keySkills} />
      <CandidateResumeProjectExpectations text={resume.projectExpectations} />
      <CandidateResumeWorkExperience workExperience={resume.WorkHistory} />
      <CandidateResumeEducation education={resume.Education} />
      <ResumeLoginBanner resumeId={resume.id} />
    </div>
  )
}
